import { useId, useRef, useState } from "react";
import { ApiError } from "../../../lib/http/errors";
import { Button } from "../../../components/ui/Button";
import { Empty, ErrorBanner } from "../../../components/Shell";
import { useT } from "../../../i18n";
import { InvalidField, NICKNAME_MAX, RealNameSuspect } from "../api/teacherApi";
import type { TeacherApi } from "../api/teacherApi";
import type { TeacherStudent } from "../types";

/**
 * 별명표(FS-11-05 · SR-SOC-03 · SR-SEC-01) — 교사 입력(학생은 편집 불가). 행마다 바로 저장(PUT …/nicknames/{pid}).
 *  - '실명 대신 별명' 고정 안내 · 실명 의심: 행 칩 '⚠ 실명 같아요' + 저장 전 경고 배너 → 교사 확인 후 confirmSuspect 저장
 *  - 미지정 행은 자동 별명을 쓴다(세션 표시 이름은 남에게 보이지 않음) · '자동 별명으로'(DELETE)
 *  - '별명표는 코스 종료 90일 후 자동 삭제' 안내 고정
 * 명부 첫 칸은 자동 별명이다 — LTI 표시 이름(실명일 수 있음)은 서버가 저장하지도 내려주지도 않는다.
 */
export function checkNickname(raw: string): "ok" | "empty" | "long" {
  const v = raw.trim();
  if (!v) return "empty";
  return [...v].length > NICKNAME_MAX ? "long" : "ok";
}

type RowState =
  | { k: "edit"; value: string; err?: string | null }
  | { k: "saving"; value: string }
  | { k: "suspect"; value: string };

export function NicknameTable({
  api,
  students,
  onChange,
}: {
  api: TeacherApi;
  students: TeacherStudent[] | null;
  onChange: (s: TeacherStudent) => void;
}) {
  const t = useT();
  const id = useId();
  const [rows, setRows] = useState<Record<string, RowState>>({});
  const [failure, setFailure] = useState<string | null>(null);
  const inputs = useRef<Record<string, HTMLInputElement | null>>({});

  const set = (pid: string, st: RowState | null) =>
    setRows((r) => {
      const next = { ...r };
      if (st) next[pid] = st;
      else delete next[pid];
      return next;
    });

  async function save(s: TeacherStudent, value: string, confirm = false) {
    const c = checkNickname(value);
    if (c !== "ok") {
      set(s.pid, {
        k: "edit",
        value,
        err: t(c === "empty" ? "teacher.nick.empty" : "teacher.nick.long", { max: NICKNAME_MAX }),
      });
      inputs.current[s.pid]?.focus();
      return;
    }
    set(s.pid, { k: "saving", value });
    setFailure(null);
    try {
      const r = await api.setNickname(s.pid, value.trim(), confirm);
      onChange({
        ...s,
        nickname: r.nickname,
        nicknameSet: true,
        realNameSuspect: r.realNameSuspect,
      });
      set(s.pid, null);
    } catch (e) {
      if (e instanceof RealNameSuspect) set(s.pid, { k: "suspect", value });
      else if (e instanceof InvalidField)
        set(s.pid, { k: "edit", value, err: t("teacher.nick.invalid", { max: NICKNAME_MAX }) });
      else {
        set(s.pid, { k: "edit", value });
        setFailure(e instanceof ApiError ? e.code : "NET-SERVER");
      }
    }
  }

  async function clear(s: TeacherStudent) {
    set(s.pid, { k: "saving", value: "" });
    try {
      await api.clearNickname(s.pid);
      onChange({ ...s, nickname: s.autoNickname, nicknameSet: false, realNameSuspect: false });
      set(s.pid, null);
    } catch (e) {
      set(s.pid, null);
      setFailure(e instanceof ApiError ? e.code : "NET-SERVER");
    }
  }

  return (
    <section className="pf-pane" aria-labelledby={`${id}-h`} data-testid="nickname-table">
      <h2 id={`${id}-h`} className="pf-h3">
        {t("teacher.nick.title")}
      </h2>
      {students === null ? (
        <p className="pf-muted">{t("common.loading")}</p>
      ) : students.length === 0 ? (
        <Empty label={t("teacher.nick.empty")} />
      ) : (
        <div
          className="pf-tbl-scroll"
          tabIndex={0}
          role="region"
          aria-label={t("teacher.nick.title")}
        >
          <table className="pf-tbl">
            <thead>
              <tr>
                <th scope="col">{t("teacher.nick.colAuto")}</th>
                <th scope="col">{t("teacher.nick.colNick")}</th>
                <th scope="col">
                  <span className="pf-sr-only">{t("teacher.nick.colAction")}</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {students.map((s) => {
                const st = rows[s.pid];
                const editing = st && st.k !== "suspect" ? st : null;
                const warn = s.realNameSuspect || st?.k === "suspect";
                const inputId = `${id}-${s.pid}`;
                return (
                  <tr
                    key={s.pid}
                    data-pid={s.pid}
                    data-suspect={s.realNameSuspect || undefined}
                    className={warn ? "pf-tbl__row--warn" : undefined}
                  >
                    <th scope="row">{s.autoNickname}</th>
                    <td>
                      {st ? (
                        <div className="pf-field-row">
                          <label htmlFor={inputId} className="pf-sr-only">
                            {t("teacher.nick.inputLabel", { name: s.autoNickname })}
                          </label>
                          <input
                            ref={(el) => {
                              inputs.current[s.pid] = el;
                            }}
                            id={inputId}
                            className="pf-input"
                            type="text"
                            value={st.value}
                            maxLength={NICKNAME_MAX + 10}
                            autoComplete="off"
                            spellCheck={false}
                            autoFocus
                            disabled={st.k === "saving"}
                            aria-invalid={editing?.k === "edit" && editing.err ? true : undefined}
                            aria-describedby={`${id}-notice`}
                            data-testid="nick-input"
                            onChange={(e) => set(s.pid, { k: "edit", value: e.target.value })}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") void save(s, st.value);
                              if (e.key === "Escape") set(s.pid, null);
                            }}
                          />
                          <span className="pf-counter">
                            {t("teacher.works.count", {
                              n: [...st.value.trim()].length,
                              max: NICKNAME_MAX,
                            })}
                          </span>
                        </div>
                      ) : s.nicknameSet ? (
                        <span
                          className={`pf-chip-static ${s.realNameSuspect ? "pf-chip-static--suspect" : "pf-chip-static--dash"}`}
                          data-testid="nick-value"
                        >
                          {s.nickname}
                        </span>
                      ) : (
                        <span
                          className="pf-chip-static pf-chip-static--dash"
                          data-testid="nick-value"
                        >
                          {t("teacher.nick.unset")}
                        </span>
                      )}{" "}
                      {warn && (
                        <span
                          className="pf-chip-static pf-chip-static--err"
                          data-testid="suspect-chip"
                        >
                          {t("teacher.nick.suspectChip")}
                        </span>
                      )}
                      {editing?.k === "edit" && editing.err && (
                        <span className="pf-field-err" role="alert">
                          {editing.err}
                        </span>
                      )}
                      {st?.k === "suspect" && (
                        <div
                          className="pf-banner pf-banner--warn"
                          role="alert"
                          data-testid="nick-suspect"
                        >
                          <div className="pf-banner__body">
                            <p className="pf-banner__text">
                              {t("teacher.nick.suspectAsk", { nick: st.value.trim() })}
                            </p>
                            <div className="pf-banner__actions">
                              <Button
                                variant="ghost"
                                className="pf-btn--sm"
                                onClick={() => set(s.pid, { k: "edit", value: st.value })}
                              >
                                {t("teacher.nick.rewrite")}
                              </Button>
                              <Button
                                variant="accent"
                                className="pf-btn--sm"
                                data-testid="nick-confirm"
                                onClick={() => void save(s, st.value, true)}
                              >
                                {t("teacher.nick.confirmSave")}
                              </Button>
                            </div>
                          </div>
                        </div>
                      )}
                    </td>
                    <td className="pf-tbl__act">
                      {editing ? (
                        <div className="pf-row pf-tbl__actions">
                          <Button
                            variant="accent"
                            className="pf-btn--sm"
                            disabled={editing.k === "saving"}
                            data-testid="nick-save"
                            onClick={() => void save(s, editing.value)}
                          >
                            {t("teacher.common.save")}
                          </Button>
                          {s.nicknameSet && (
                            <Button
                              variant="ghost"
                              className="pf-btn--sm"
                              disabled={editing.k === "saving"}
                              onClick={() => void clear(s)}
                            >
                              {t("teacher.nick.useAuto")}
                            </Button>
                          )}
                          <Button
                            variant="ghost"
                            className="pf-btn--sm"
                            disabled={editing.k === "saving"}
                            onClick={() => set(s.pid, null)}
                          >
                            {t("teacher.common.cancel")}
                          </Button>
                        </div>
                      ) : (
                        !st && (
                          <Button
                            variant="ghost"
                            className="pf-btn--sm"
                            data-testid="nick-edit"
                            aria-label={t("teacher.nick.editLabel", { name: s.autoNickname })}
                            onClick={() =>
                              set(s.pid, { k: "edit", value: s.nicknameSet ? s.nickname : "" })
                            }
                          >
                            {t("teacher.nick.change")}
                          </Button>
                        )
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      {failure && (
        <ErrorBanner
          cause={t("teacher.nick.saveFailed")}
          action={t("teacher.common.retryLater")}
          code={failure}
        />
      )}
      <div
        className="pf-banner pf-banner--warn"
        role="note"
        id={`${id}-notice`}
        data-testid="realname-notice"
      >
        <p className="pf-banner__text">
          ⚠ <b>{t("teacher.nick.noticeStrong")}</b>
          {t("teacher.nick.noticeRest")}
        </p>
      </div>
      <p className="pf-note" data-testid="purge-notice">
        {t("teacher.nick.footA")}
        <b>{t("teacher.nick.footPurge")}</b>
        {t("teacher.nick.footB")}
      </p>
    </section>
  );
}
